import React, { useState, useEffect } from 'react';
import Modal from './Modal';

/**
 * PromptModal Component
 *
 * A reusable modal dialog with text input for collecting user input.
 * Replaces window.prompt() with a proper React modal interface.
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed/cancelled
 * @param {function} onConfirm - Callback when user confirms (receives input value)
 * @param {string} title - Modal title
 * @param {string} message - Prompt message/question
 * @param {string} placeholder - Input placeholder text
 * @param {string} confirmText - Confirm button text (default: "Submit")
 * @param {string} cancelText - Cancel button text (default: "Cancel")
 * @param {string} initialValue - Initial input value (default: "")
 * @param {boolean} required - Whether input is required (default: true)
 */
export default function PromptModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  placeholder = '',
  confirmText = 'Submit',
  cancelText = 'Cancel',
  initialValue = '',
  required = true
}) {
  const [inputValue, setInputValue] = useState(initialValue);

  // Reset input when modal opens
  useEffect(() => {
    if (isOpen) {
      setInputValue(initialValue);
    }
  }, [isOpen, initialValue]);

  const handleConfirm = () => {
    if (required && !inputValue.trim()) {
      return; // Don't submit if required and empty
    }
    onConfirm(inputValue.trim());
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <p className="text-gray-700 mb-4">{message}</p>

        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          autoFocus
        />

        {required && (
          <p className="text-sm text-gray-500 mt-2">
            * This field is required
          </p>
        )}
      </div>

      <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {cancelText}
        </button>
        <button
          onClick={handleConfirm}
          disabled={required && !inputValue.trim()}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {confirmText}
        </button>
      </div>
    </Modal>
  );
}
